export const hasValue = (value) =>
  value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value));

export const formatLabel = (value) =>
  value
    ? String(value).replaceAll("_", " ").toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase())
    : null;

export const formatAvailableLabel = (value) =>
  value && String(value).toUpperCase() !== "NOT_AVAILABLE"
    ? formatLabel(value)
    : "Not available";

const firstArray = (...values) => values.find(Array.isArray) || [];

// The optimizer summary is the single source of truth for portfolio funding.
// Presentation components consume this contract rather than independently
// summing scenario rows or relying on similarly named legacy fields.
export const getCapitalAllocationValues = (decision) => {
  const summary = decision?.summary || {};
  const request = decision?.request || {};
  const availableBudgetValue = summary?.portfolio_budget ?? request?.portfolio_budget;
  const allocatedCapitalValue = summary?.total_funding_requirement;
  const remainingBudgetValue = summary?.remaining_budget;
  const availableBudget = hasValue(availableBudgetValue) ? Number(availableBudgetValue) : null;
  const allocatedCapital = hasValue(allocatedCapitalValue) ? Number(allocatedCapitalValue) : null;
  const reportedRemainingBudget = hasValue(remainingBudgetValue) ? Number(remainingBudgetValue) : null;

  return {
    availableBudget: Number.isFinite(availableBudget) ? availableBudget : null,
    allocatedCapital: Number.isFinite(allocatedCapital) ? allocatedCapital : null,
    remainingBudget: Number.isFinite(reportedRemainingBudget)
      ? reportedRemainingBudget
      : Number.isFinite(availableBudget) && Number.isFinite(allocatedCapital)
        ? Math.max(availableBudget - allocatedCapital, 0)
        : null,
  };
};

// Normalized executive metrics are intentionally mapped once here. Components
// may format these values differently, but must not select competing response
// fields or recreate portfolio-level calculations.
export const getExecutiveKpiValues = (decision) => {
  const metrics = decision?.metrics || {};
  const capitalAllocation = getCapitalAllocationValues(decision);

  return {
    ...capitalAllocation,
    optimizerBaselineEal: metrics?.eal?.baseline,
    recommendedEal: metrics?.eal?.selected,
    ealReduction: metrics?.eal?.reduction,
    optimizerBaselineP95: metrics?.p95?.baseline,
    portfolioP95: metrics?.p95?.selected,
    p95Reduction: metrics?.p95?.reduction,
    unfundedCapitalAtRiskReduction: metrics?.capital_at_risk?.reduction,
    portfolioEfficiency: metrics?.capital_allocation?.portfolio_funding_efficiency,
    scenarioAppetiteMetCount: metrics?.risk_appetite?.met_count,
    scenarioAppetiteGapCount: metrics?.risk_appetite?.not_met_count,
    totalPortfolioP95: metrics?.funding_mix?.total_p95,
    retainedP95: metrics?.funding_mix?.retained_p95,
    unfundedCapitalAtRisk: metrics?.funding_mix?.unfunded_capital_at_risk,
  };
};

export const isRecommendationFeasible = (decision) =>
  Boolean(decision) &&
  decision?.recommendation_valid !== false &&
  !["INFEASIBLE", "FAILED"].includes(String(decision?.status || "").toUpperCase()) &&
  decision?.feasible !== false;

export const getNarrativeViewModel = (decision, activeScenarios, settings, currentPortfolioPosition) => {
  const summary = decision?.summary || {};
  const metrics = decision?.metrics || {};
  const recommendation = decision?.executive_recommendation || {};
  const target = decision?.target_portfolio || decision?.approved_target || metrics?.target || null;
  const protection = decision?.current_protection_baseline || decision?.protection_baseline || null;
  const executiveKpis = getExecutiveKpiValues(decision);

  return {
    decision,
    settings,
    currency: settings?.currency || decision?.currency,
    eal: {
      baseline: executiveKpis.optimizerBaselineEal,
      selected: executiveKpis.recommendedEal,
      reduction: executiveKpis.ealReduction,
    },
    p95: {
      baseline: executiveKpis.optimizerBaselineP95,
      selected: executiveKpis.portfolioP95,
      reduction: executiveKpis.p95Reduction,
    },
    riskAppetite: {
      met_count: executiveKpis.scenarioAppetiteMetCount,
      not_met_count: executiveKpis.scenarioAppetiteGapCount,
    },
    scenarioCount:
      currentPortfolioPosition?.activeScenarios ??
      activeScenarios?.length ??
      summary?.scenario_count,
    currentCapital: summary?.current_investment ?? summary?.committed_capital,
    position: recommendation?.executive_position || {},
    headline: recommendation?.headline,
    observations: Array.isArray(recommendation?.observations) ? recommendation.observations : [],
    target,
    protection,
    protections: firstArray(protection?.items, protection?.protections, decision?.existing_protections),
    selectedActions: firstArray(decision?.selected_actions, recommendation?.selected_actions),
    alternatives: firstArray(decision?.alternatives, recommendation?.alternatives),
    feasible: isRecommendationFeasible(decision),
    ...executiveKpis,
    expectedBenefit: summary?.expected_benefit ?? metrics?.expected_benefit,
    objective: decision?.objective,
    currentBaseline: {
      expectedAnnualLoss:
        currentPortfolioPosition?.expectedAnnualLoss ??
        metrics?.eal?.baseline,
      capitalAtRiskP95:
        currentPortfolioPosition?.capitalAtRiskP95 ??
        metrics?.p95?.baseline,
      scenariosWithProtectionGaps:
        currentPortfolioPosition?.scenariosWithProtectionGaps,
      riskAppetitePosition:
        currentPortfolioPosition?.riskAppetitePosition ??
        recommendation?.executive_position
          ?.risk_appetite_position,
    },
  };
};

export const getBusinessImpactValues = (decision) => {
  const kpis = getExecutiveKpiValues(decision);
  return [
    ["Optimizer-Evaluated Baseline EAL", kpis.optimizerBaselineEal, "money", "Pre-allocation EAL for the scenarios evaluated by the optimizer"],
    ["Recommended Portfolio EAL", kpis.recommendedEal, "money", "Post-allocation EAL for the scenarios evaluated by the optimizer"],
    ["Optimizer-Calculated EAL Reduction", kpis.ealReduction, "money", "Compared with the optimizer’s evaluated scenario baseline, not the organizational Current Baseline"],
    ["Optimizer-Evaluated Baseline P95", kpis.optimizerBaselineP95, "money", "Pre-allocation P95 for the scenarios evaluated by the optimizer"],
    ["Projected P95", kpis.portfolioP95, "money"],
    ["Financing Capital at Risk Reduction", kpis.unfundedCapitalAtRiskReduction, "money"],
    ["Portfolio Efficiency", kpis.portfolioEfficiency, "percent"],
  ].filter(([, value]) => hasValue(value));
};

export const getAlternatives = (decision) =>
  Array.isArray(decision?.alternatives) ? decision.alternatives : [];
